import React, { useState, useEffect } from 'react';
import { ravanaPackages, getPackageComparison } from '../../../data/ravanaPackages';

const PackageManager = () => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [comparison, setComparison] = useState([]);

  useEffect(() => {
    // Load packages from the data file
    const allPackages = Object.values(ravanaPackages);
    setPackages(allPackages);
    setComparison(getPackageComparison());
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-white font-mono p-6">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Package Management</h2>
        <p className="text-gray-400">Manage Ravana Migration packages and pricing</p>
      </div>

      {/* Package Cards */}
      <div className="admin-grid-3">
        {packages.map((pkg, index) => (
          <div
            key={pkg.id}
            className={`bg-gray-800 border rounded-lg shadow-sm relative ${pkg.id === 'pro' ? 'border-blue-500' : 'border-gray-700'}`}
          >
            {pkg.id === 'pro' && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                <span className="inline-flex items-center px-3 py-1 text-xs font-bold text-green-400 bg-green-900/30 border border-green-800 rounded-full">
                  ⭐ RECOMMENDED
                </span>
              </div>
            )}

            <div className="p-6 text-center">
              <h3 className="text-xl font-bold text-white mb-2">{pkg.name}</h3>
              <div className="flex items-baseline justify-center gap-2 mb-3">
                <span className="text-3xl font-extrabold text-blue-400">
                  {pkg.priceDisplay}
                </span>
                <span className="text-sm text-gray-400 font-medium">LKR</span>
              </div>
              <p className="text-sm text-gray-400 mb-4">{pkg.description}</p>

              <div className="mb-4 text-left">
                <h4 className="text-sm font-bold text-white mb-2 uppercase tracking-wider">Key Features:</h4>
                <ul className="space-y-2">
                  {pkg.features.slice(0, 5).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-4 pt-4 border-t border-gray-700">
                <div className="space-y-2">
                  <div className="bg-blue-900/20 rounded p-3 border border-blue-800">
                    <p className="text-xs font-semibold text-blue-400 mb-1">Payment Plan</p>
                    <p className="text-sm font-bold">
                      {pkg.paymentPlan.upfront.toLocaleString()} upfront
                    </p>
                    {pkg.paymentPlan.afterOffer > 0 && (
                      <p className="text-xs text-gray-400">
                        + {pkg.paymentPlan.afterOffer.toLocaleString()} after offer
                      </p>
                    )}
                    {pkg.paymentPlan.final > 0 && (
                      <p className="text-xs text-gray-400">
                        + {pkg.paymentPlan.final.toLocaleString()} final
                      </p>
                    )}
                  </div>
                  <div className="bg-gray-900/50 rounded p-3 border border-gray-700">
                    <p className="text-xs font-semibold text-gray-300 mb-1">Refund Policy</p>
                    <p className="text-sm font-bold">{pkg.refundNote}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setSelectedPackage(pkg)}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Package Comparison Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-sm mt-8">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-medium text-center text-white">Package Comparison</h3>
        </div>
        <div className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th className="p-3 bg-blue-900/50 text-white border-b border-gray-700">Feature</th>
                  <th className="p-3 bg-blue-900/50 text-white text-center border-b border-gray-700">DIY</th>
                  <th className="p-3 bg-blue-900/50 text-white text-center border-b border-gray-700">Plus</th>
                  <th className="p-3 bg-blue-900/50 text-white text-center border-b border-gray-700">PRO</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700 text-gray-300">
                {comparison.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-700/50">
                    <td className="p-3 font-medium">{row.feature}</td>
                    <td className="p-3 text-center">{row.diy}</td>
                    <td className="p-3 text-center">{row.plus}</td>
                    <td className="p-3 text-center">{row.pro}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Entry Requirements */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-sm mt-8">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-medium text-center text-white">Entry Requirements (All Packages)</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-900/20 rounded p-4 border border-blue-800">
              <div className="text-center mb-3">
                <div className="text-3xl mb-2">📚</div>
                <h4 className="font-bold text-white">Minimum One Required:</h4>
              </div>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• A/L 3 Passes (any stream)</li>
                <li>• Bachelor's Degree or Equivalent</li>
                <li>• IELTS 6.0 (can apply without test)</li>
              </ul>
            </div>
            <div className="bg-green-900/20 rounded p-4 border border-green-800">
              <div className="text-center mb-3">
                <div className="text-3xl mb-2">💰</div>
                <h4 className="font-bold text-white">Financial:</h4>
              </div>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• Show Money: LKR 5M minimum</li>
              </ul>
            </div>
            <div className="bg-purple-900/20 rounded p-4 border border-purple-800">
              <div className="text-center mb-3">
                <div className="text-3xl mb-2">✨</div>
                <h4 className="font-bold text-white">Additional Benefits:</h4>
              </div>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• Contract-first pricing</li>
                <li>• Bank transfer only</li>
                <li>• Fintiba Official Partner</li>
                <li>• 100% monitored timelines</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Flight Plan™ Alliance Info */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-sm mt-8">
        <div className="p-6 text-center">
          <h3 className="text-xl font-bold text-white mb-3">🌏 Flight Plan™ Alliance Partner</h3>
          <p className="text-lg font-semibold text-gray-300 mb-4">
            One Application. Five Destinations. One Global Partner.
          </p>
          <p className="text-gray-400 mb-4">
            Apply to Germany, Canada, Australia, United Kingdom, and New Zealand with one profile.
            Our expert team manages everything from university shortlisting and visa documentation
            to travel and settlement support.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {['🇩🇪 Germany', '🇨🇦 Canada', '🇦🇺 Australia', '🇬🇧 UK', '🇳🇿 New Zealand'].map((country, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-400 bg-blue-900/30 rounded border border-blue-800"
              >
                {country}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Success Metrics */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-sm mt-8">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-medium text-center text-white">✨ Success Metrics</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { value: '99%', label: 'Visa Success Rate', icon: '🎯', color: 'text-green-400' },
              { value: '5', label: 'Countries Available', icon: '🌍', color: 'text-blue-400' },
              { value: '100%', label: 'Monitored Timelines', icon: '⏱️', color: 'text-purple-400' }
            ].map((metric, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl mb-2">{metric.icon}</div>
                <div className={`text-3xl font-extrabold ${metric.color} mb-2`}>
                  {metric.value}
                </div>
                <div className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal for Package Details */}
      {selectedPackage && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex justify-between items-start mb-6 pb-4 border-b border-gray-700 p-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">{selectedPackage.name}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-extrabold text-blue-400">
                    {selectedPackage.priceDisplay}
                  </span>
                  <span className="text-sm text-gray-400 font-medium">LKR</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedPackage(null)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6 p-6 pt-0">
              <div className="bg-blue-900/20 rounded p-4 border border-blue-800">
                <p className="text-gray-300">{selectedPackage.description}</p>
              </div>

              <div>
                <h4 className="text-lg font-bold text-white mb-4">All Features:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedPackage.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-gray-700/50 rounded border border-gray-600">
                      <span className="text-green-400 mt-1">✓</span>
                      <span className="text-sm text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-900/20 rounded p-4 border border-blue-800">
                  <h4 className="font-bold text-white mb-3">Payment Plan</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex justify-between items-center py-2 border-b border-blue-800">
                      <span className="text-gray-400">Upfront:</span>
                      <span className="font-bold text-gray-200">{selectedPackage.paymentPlan.upfront.toLocaleString()} LKR</span>
                    </li>
                    {selectedPackage.paymentPlan.afterOffer > 0 && (
                      <li className="flex justify-between items-center py-2 border-b border-blue-800">
                        <span className="text-gray-400">After Offer:</span>
                        <span className="font-bold text-gray-200">{selectedPackage.paymentPlan.afterOffer.toLocaleString()} LKR</span>
                      </li>
                    )}
                    {selectedPackage.paymentPlan.final > 0 && (
                      <li className="flex justify-between items-center py-2">
                        <span className="text-gray-400">Final:</span>
                        <span className="font-bold text-gray-200">{selectedPackage.paymentPlan.final.toLocaleString()} LKR</span>
                      </li>
                    )}
                  </ul>
                  <div className="mt-3 pt-3 border-t border-blue-800">
                    <p className="text-xs font-semibold text-blue-400 mb-1">Refund Policy</p>
                    <p className="text-sm font-bold text-gray-300">{selectedPackage.refundNote}</p>
                  </div>
                </div>

                <div className="bg-purple-900/20 rounded p-4 border border-purple-800">
                  <h4 className="font-bold text-white mb-3">Target Audience</h4>
                  <p className="text-sm text-gray-300">{selectedPackage.targetAudience}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageManager;
